import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx } from '../utils/cx.js';

/**
 * C-08 カード。地は bg/card、角丸 radius/card、ライトは elev/1、ダークは border/strong 1px（--card-border-color）。
 * tone: 通常 / 警告枠（未送信・差し戻し）/ 危険枠（期限超過）
 */
export interface CardProps extends ComponentPropsWithoutRef<'div'> {
  tone?: 'default' | 'warning' | 'danger';
  padding?: 'none' | 'sm' | 'md';
  /** クリック可能な行として使う（ホバー） */
  interactive?: boolean;
}

export function Card({ tone = 'default', padding = 'md', interactive = false, className, children, ...rest }: CardProps) {
  return (
    <div
      className={cx(
        'rounded-card bg-card shadow-elev-1 border',
        tone === 'default' && 'border-[var(--card-border-color)]',
        tone === 'warning' && 'border-2 border-warning',
        tone === 'danger' && 'border-2 border-danger',
        padding === 'md' && 'p-5',
        padding === 'sm' && 'p-4',
        interactive && 'transition-colors hover:bg-sunken/60',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cx('mb-4 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="text-headline">{title}</h2>
        {description ? <p className="text-callout text-fg-secondary">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/** 意味のある区切り線（border/strong）。装飾には <hr className="border-line" /> を使う */
export function Divider({ strong = true, className }: { strong?: boolean; className?: string }) {
  return <hr className={cx('my-5 border-0 border-t', strong ? 'border-line-strong' : 'border-line', className)} />;
}
